import axios from 'axios';
import { useCallback, useMemo, useState, useEffect } from 'react';
import useSWR from 'swr';

import { GridColDef, GridSelectionModel } from '@mui/x-data-grid-pro';
import { dataGridCol } from '@shared/components/grid/common';
import { withQueryParams } from '@shared/commonFunctions';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { AlertModal } from '@shared/components/modals/AlertModal';
import { SERVICES } from '@shared/constants/apiConfig';
import { Wrapper } from '@styles/CommonStyles';
import { useUserAccount } from '@shared/hooks/useUserAccount';
import { useServerSide } from '@shared/hooks/useServerSide';
import { PhoenixGridHeader } from '@modules/admin/Tools/LenderUtility/shared/PhoenixGridHeader';
import { CancelButton } from '@shared/inputs/Buttons';
import { ReadyToShipModal } from './components/ReadyToShipModal';
import { IBatchDeal, IReadyShipment, IReadyToShip, IShipment } from './interfaces';
import { requestCreateReadytoship, requestDeleteDealShipments } from './requests';

export const ReadytoShip = () => {
  const pageSize = 15;
  const { userId, username } = useUserAccount();
  const { requestPayload, onSortModelChange, onFilterModelChange, onPageChange } = useServerSide();
  const [items, setItems] = useState<IShipment[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);

  const { data: fedexShipments, mutate, isLoading } = useSWR<IReadyShipment>(
  withQueryParams(`/${SERVICES.DEAL}/deals/filtered/shipments`),
  (url: string) =>
    axios.post(url, { ...requestPayload, pageSize: pageSize }),
    { revalidateIfStale: true}
  );
  const [selectedIds, setSelectedIds] = useState<GridSelectionModel>([]);
  const [currentReadyToShip, setCurrentReadyToShip] = useState<IReadyToShip | null>(null);
  const [currentReadyToShipPosition, setCurrentReadyToShipPosition] = useState(0);
  const [openAlertModal, setOpenAlertModal] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    mutate();
  }, [requestPayload]);

  useEffect(() => {
    if (fedexShipments) {
      setItems(fedexShipments?.data?.items);
      setTotalCount(fedexShipments?.data?.totalCount);
    }
  }, [fedexShipments]);

  const columns: GridColDef[] = useMemo(() => [
    dataGridCol('dealNumericId', {
      headerName: 'Deal',
      flex: 0.6,
      minWidth: 50
    }),
    dataGridCol('contactName', {
      flex: 1,
      minWidth: 100
    }),

    dataGridCol('workflow', {
      headerName: 'Workflow Step',
      flex: 2,
      minWidth: 100,
    }),
    dataGridCol('titleAgent', {
      headerName: 'Agent',
      flex: 1,
      minWidth: 100,
    }),
    dataGridCol('registrationState', {
      headerName: 'Reg St',
      flex: 1,
      minWidth: 100
    }),
    dataGridCol('checkPayee', {
      flex: 1.6,
      minWidth: 100,
    }),
    dataGridCol('vin', {
      flex: 1.5,
      minWidth: 120,
    }),
    dataGridCol('lenderName', {
      flex: 1.3,
      minWidth: 120,
    }),
  ], []);

  const onCreateShipment = useCallback(() => {
    const filtered = fedexShipments?.data?.items?.filter((shipment) => selectedIds.includes(shipment.dealId));
    const deals: IBatchDeal[] = filtered?.map(({ dealId, titleAgent }) => ({ dealId, titleAgentName: titleAgent })) || [];
    const initData = { dealsInThisBatch: deals, userId } as IReadyToShip;
    setCurrentReadyToShip(initData);
    setCurrentReadyToShipPosition(prevState => prevState + 1);
  }, [fedexShipments?.data?.items, selectedIds, userId]);

  const onRemoveClick = useCallback(() => {
    setOpenAlertModal(true);
  }, []);

  const handleCheckboxSelection = useCallback((ids: GridSelectionModel) => {
    setSelectedIds(ids);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    setLoading(true);
    await requestDeleteDealShipments(selectedIds);
    setLoading(false);
    setOpenAlertModal(false); 
    mutate();
  }, [selectedIds]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSubmit = useCallback(
    async (data: IReadyToShip) => {
      data.shipmentAddressProfileId = data.shipmentAddressProfileId === '' ?  null : data.shipmentAddressProfileId;
      data.shipTo = data.name;
      data.userId = userId ?? '';
      data.shipTo2 = data.company;
      setLoading(true);
      await requestCreateReadytoship(data).then(() => {
        mutate();
        setCurrentReadyToShip(null);
        setCurrentReadyToShipPosition(0);
        setSelectedIds([]);
      });
      setLoading(false);
    },
    [userId, username]
  );

  const handleClose = useCallback(() => {
    setCurrentReadyToShip(null);
    setCurrentReadyToShipPosition(0);
    setSelectedIds([]);
  }, []);

  return (
    <Wrapper>
      <AlertModal
        title='Confirm delete'
        subtitle='Are you sure you want to delete these shipment details'
        buttonText='Ok'
        open={openAlertModal}
        isLoading={loading}
        onConfirm={handleConfirmDelete}
        onClose={() => setOpenAlertModal(false)}
      />
      <ReadyToShipModal
        modalIsOpen={!!currentReadyToShip}
        currentReadyToShipPosition={currentReadyToShipPosition}
        handleSubmit={handleSubmit}
        handleClose={handleClose}
        loading={loading}
        currentReadyToShip={currentReadyToShip}
      />
      <PhoenixGridHeader title=''>
        <CancelButton variant="contained" label="Remove From List" sx={{ position: 'relative', top: '40px', right: '135px' }} onClick={onRemoveClick} />
      </PhoenixGridHeader>
      <PhoenixGrid
        autoHeight
        withHeader
        strict
        checkboxSelection
        showClearAllFilter
        keepNonExistentRowsSelected
        paginationMode="server"
        sortingMode='server'
        filterMode='server'
        name="Ready to Ship"
        buttonTitle="Create Shipment"
        pageSize={pageSize}
        columns={columns}
        loading={isLoading}
        rows={items && !isLoading ? items : []}
        disabled={!selectedIds.length}
        getRowId={(row) => row.dealId}
        rowCount={totalCount}
        selectionModel={selectedIds}
        onSortModelChange={onSortModelChange}
        onFilterModelChange={onFilterModelChange}
        onButtonClick={onCreateShipment}
        onSelectionModelChange={handleCheckboxSelection}
        onPageChange={onPageChange}
      />
    </Wrapper>
  );
};
